import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Project, Status } from 'data/project-management/projects';
import Avatar from 'components/base/Avatar';
import { Col, Form, Modal, ProgressBar, Row } from 'react-bootstrap';
import classNames from 'classnames';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import ActionDropdownItems from 'components/common/UsersTokensActionDropdownItems';
import AvatarDropdown from 'components/common/AvatarDropdown';
import { faFileExport, faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import { ChangeEvent, useState } from 'react';
import AvatarDropzone from 'components/common/AvatarPTSDropzone';
import avatarPlaceholder from 'assets/img/team/avatar.webp';
import ReactSelect from 'components/base/ReactSelect';
import { INeuroPrompt } from '../../data/models/neuro/INeuroPrompt';
import { useUserContext } from 'layouts/UserContext';
import neuroImg from 'assets/img/Anubis/Hazball.png';

const columns: ColumnDef<INeuroPrompt>[] = [
  {
    accessorKey: 'name',
    header: 'Neuro Prompt',
    cell: ({ row: { original } }) => {
      const { title } = original;
      return (
        <Link
          to="#"
          className="text-decoration-none fw-bold fs-9"
        >
          {title}
        </Link>
      );
    },
    meta: {
      cellProps: { className: 'white-space-nowrap' },
      headerProps: { style: { width: '30%' } }
    }
  },
  {
    id: 'assigness',
    header: 'NeuroSkills',
    cell: ({ row: { original } }) => {
      const { neuroSkillIds } = original; // INeuroPrompt[]

      const dummyUser = {
        id: 1,
        name: 'Dummy',
        avatar: neuroImg,
        username: 'dummyuser',
        connections: 0,
        mutual: 0
      }

      if (neuroSkillIds) {
        return (
          <Avatar.Group
            total={neuroSkillIds.length}
            size="s"
            className="overflow-hidden"
          >
            {neuroSkillIds.slice(0, 4).map(neuroSkill => (
              <AvatarDropdown user={dummyUser} size="s" key={neuroSkill._id} />
            ))}
          </Avatar.Group>
        );
      }
    },
    meta: {
      cellProps: { className: 'ps-3' },
      headerProps: { style: { width: '10%' }, className: 'ps-3' }
    }
  },
  {
    id: 'conditionTags',
    header: 'Condition Tags',
    cell: ({ row: { original } }) => {
      const { conditionTags } = original;
      if (!conditionTags || conditionTags.length === 0) {
        return <span className="text-body-secondary">No tags</span>;
      }
      return (
        <div>
          {conditionTags.map(tag => (
            <span key={tag} className="badge bg-secondary me-1">{tag}</span>
          ))}
        </div>
      );
    },
    meta: {
      cellProps: { className: 'ps-3' },
      headerProps: { style: { width: '15%' }, className: 'ps-3' }
    }
  },
  {
    header: 'Register date',
    accessorKey: 'createdAt',
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-body white-space-nowrap' },
      headerProps: { style: { width: '10%' }, className: 'ps-3' }
    }
  },
  {
    header: 'Last Update',
    accessorKey: 'updatedAt',
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-body white-space-nowrap' },
      headerProps: { style: { width: '15%' }, className: 'ps-3' }
    }
  },
  {
    id: 'action',
    cell: () => (
      <RevealDropdownTrigger>
        <RevealDropdown>
          <ActionDropdownItems />
        </RevealDropdown>
      </RevealDropdownTrigger>
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  }
];

const NeuroSkillsDashboardTable = () => {
  const { user } = useUserContext();
  const table = useAdvanceTable({
    data: user?.botInfo.neuroPrompts as INeuroPrompt[],
    columns,
    pageSize: 6,
    pagination: true,
    sortable: true
  });
  
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [avatar, setAvatar] = useState(avatarPlaceholder);

  const onDrop = (acceptedFiles: File[]) => {
    setAvatar(URL.createObjectURL(acceptedFiles[0]));
  };

  return (
    <>
    <div>
      <Row>
        <div className="mb-4">
          <Row className="g-3">
            <Col xs="auto">
              <SearchBox
                placeholder="Search Prompts"
                onChange={handleSearchInputChange} />
            </Col>
            <Col
              xs="auto"
              className="scrollbar overflow-hidden-y flex-grow-1"
            ></Col>
            <Col xs="auto">
              <Button variant="primary" onClick={handleShow}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add prompt
              </Button>
            </Col>
          </Row>
        </div>
      </Row>

      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Add new prompt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Lists: [group prompts], avatar
          <Form>
            <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
              <Form.Label>Prompt Title</Form.Label>
              <Form.Control type="text" placeholder="Title new prompt" autoFocus />
            </Form.Group>
            <Form.Group className="mb-3" controlId="exampleForm.ControlTextarea1">
              <Form.Label>Prompt conditions</Form.Label>
              <Form.Control as="textarea" rows={3} placeholder="List of conditions" />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Applied Neuriskills</Form.Label>
              <ReactSelect
                options={[
                   {
                    value: 'all',
                    label: 'Add All neuroskills registered'
                  }
                ]}
                isMulti
                placeholder="Select neuroskills registered..."
              />
              <Form.Control.Feedback type="invalid">
                This field is required.
              </Form.Control.Feedback>
            </Form.Group>
            <Row className="mb-4">
              <Col md="auto">
                <Avatar src={avatar} placeholder size="3xl" />
              </Col>
              <Col>
                <AvatarDropzone onDrop={onDrop} />
              </Col>
            </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleClose}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
    
    {user?.botInfo.neuroPrompts?.length ? (
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          tableProps={{
            className: 'phoenix-table border-top border-translucent fs-9'
          }}
        />
        <AdvanceTableFooter pagination />
      </AdvanceTableProvider>
    ) : (
      <div className="text-center py-6 px-4 bg-body-secondary border rounded-3">
        <p className="fs-7 fw-bold mb-1">🧠 No se han detectado prompts</p>
        <p className="mb-0 text-body-secondary">
          Comience creando uno ahora mismo haciendo clic en {' '}
          <strong>"Add prompt"</strong>.
        </p>
      </div>
    )}

    </div></>
  );
};

export default NeuroSkillsDashboardTable;